import React from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useAuth } from 'hooks/AuthContext'

/**
 * Locks a logged-in user to /change-password-required whenever their
 * account still has a system-generated password (e.g. a Pilot
 * admin-created account) - covers every route in the app, not just the
 * workspace handoff, since navigating to any other page (clicking the
 * logo, typing a URL, the back button) previously let the requirement be
 * skipped entirely as long as the session JWT was still valid.
 *
 * Wraps the whole route tree in App.tsx except /change-password-required
 * itself, which must stay outside this gate or every render would redirect
 * to itself.
 */
const RequireChangePasswordGate: React.FC = () => {
    const { loading, user } = useAuth()
    const location = useLocation()

    // Not logged in, or still resolving auth state - let public pages
    // render normally; there is nothing to gate for an anonymous visitor.
    if (loading || !user) {
        return <Outlet />
    }

    if (user.mustChangePassword) {
        return <Navigate to="/change-password-required" state={{ redirect: location.pathname }} replace />
    }

    return <Outlet />
}

export default RequireChangePasswordGate
